import { useSnapshot } from "valtio";
import { FontEntry, Step, StepOption } from "../../../types/wizard";
import React, { useRef, useState } from "react";
import useOutsideClick from "../../../hooks/useOutsideClick";
import { saveEmblemData, setStepCallback, validateWizardSteps, wizardStore } from "../../../stores/wizard";

interface DropdownProps {
  step: Step;
}

const Scrolling: React.FC<DropdownProps> = ({ step }) => {
  const wizSnap = useSnapshot(wizardStore);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<FontEntry | null>(null);
  const [lineOfText, setLineOfText] = useState<string>("");
  const selectRef = useRef(null);

  useOutsideClick([selectRef], () => {
    setIsOpen(false);
  });

  const handleSave = (font?: FontEntry, line?: string) => {
    const selectedValue = font ?? selectedOption;
    const text = line ?? lineOfText;

    if (!step?.id || (!selectedValue) || !text) {
      setIsOpen(false);
      return;
    }

    const option = {
      scrollingText: text,
      scrollingFont: selectedValue.key,
      scrollingFontDisplayName: selectedValue.value,
    }

    const updatedStepContent = {
      ...wizSnap.emblemData[step.id],
      ...option
    };

    const setCallback = setStepCallback[step.id] as ((content: StepOption, stepId: string) => void);
    if (setCallback) {
      setCallback(updatedStepContent, step.id);
      validateWizardSteps();
    } else {
      console.warn(`No callback defined for step: ${step.id}`);
    }

    saveEmblemData(step.id, option);
    setIsOpen(false);
  };

  return (
    <React.Fragment>
      <div className="w-full">
        <label className="block uppercase font-bold mb-2.5">Line of text</label>
        <input
          type="text"
          placeholder="Enter line of text"
          id="line-text"
          name="line-text"
          maxLength={25}
          onChange={(e) => {
            setLineOfText(e.target.value)
            handleSave(undefined, e.target.value)
          }}
          className="p-2.5 w-full border-b-2 rounded outline-none"
          style={{ border: '1px solid rgb(204, 204, 204)' }} />
        <div className="mb-4">
          <span className="text-xs italic text-gray-400">Up to 25 characters</span>
        </div>
      </div>
      <div className="w-full" ref={selectRef}>
        <label className="block uppercase font-bold mb-2.5">Font Style</label>
        <div
          className="p-2.5 cursor-pointer relative rounded bg-white"
          onClick={() => setIsOpen(!isOpen)}
          style={{ border: "1px solid #ccc" }}
        >
          {selectedOption ? (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span>{selectedOption.value}</span>
            </div>
          ) : (
            <div className="flex flex-row justify-between items-center">
              <span style={{ display: "flex" }}>Font Style</span>
              <div className="arrow"></div>
            </div>
          )}
        </div>

        {isOpen && (
          <div className="relative w-full">
            <ul
              className="m-0 p-0 rounded bg-white overflow-y-auto absolute"
              style={{
                listStyle: "none",
                border: "1px solid #ccc",
                maxHeight: "200px",
                width: "inherit",
                zIndex: 1,
              }}
            >
              {step?.fonts?.map((option: FontEntry) => (
                <li
                  key={option.key}
                  onClick={() => {
                    setSelectedOption(option)
                    handleSave(option)
                  }}
                  className="flex items-center p-2 cursor-pointer"
                  style={{
                    gap: "10px",
                    borderBottom: "1px solid #eee",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f5f5f5")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  <span>{option.value}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default Scrolling;
